import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskLines, FadeUp, Eyebrow } from "../components/site/Motion";
import { Marquee } from "../components/site/Marquee";
import { CtaBand } from "../components/site/CtaBand";
import { Magnetic } from "../components/site/Magnetic";
import { ScrollReveal } from "../components/site/ScrollReveal";
import { INTRO_DELAY } from "../components/site/Preloader";
import { useLang } from "../i18n/LangContext";
import { AREA_IMG, IMG, BLOG_URL } from "../data/content";

const D = INTRO_DELAY;

const Hero = () => {
  const { t } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const floatY = useTransform(scrollYProgress, [0, 1], ["0%", "-60%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} data-testid="home-hero" onMouseMove={onMove} className="relative overflow-hidden pt-32 lg:min-h-screen lg:pt-40">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: D }}>
            <Eyebrow>{h.eyebrow}</Eyebrow>
          </motion.div>
          <MaskLines
            key={h.lines[0]}
            lines={[h.lines[0], h.lines[1], <em key="e" className="text-[#C85A32]">{h.lines[2]}</em>]}
            className="font-serif text-[15vw] font-medium leading-[0.92] tracking-tight text-[#1C1917] sm:text-8xl lg:text-[7.5rem] xl:text-[8.5rem] [&>span]:whitespace-nowrap"
            delay={0.15 + D}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 + D }}
            className="mt-10 max-w-lg text-base leading-relaxed text-stone-600 sm:text-lg"
          >
            {h.text}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9 + D }} className="mt-10 flex flex-wrap gap-4">
            <Magnetic>
              <Link to="/chi-siamo" data-testid="hero-chi-siamo-btn" className="group inline-flex items-center gap-3 rounded-full bg-[#1C1917] px-7 py-4 text-sm font-medium text-[#FBF9F5] transition-colors duration-300 hover:bg-[#C85A32]">
                {h.cta1} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Magnetic>
            <Magnetic>
              <a href={BLOG_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-blog-btn" className="inline-flex items-center gap-2 rounded-full border border-[#D6CEBF] px-7 py-4 text-sm font-medium text-[#1C1917] transition-colors duration-300 hover:border-[#1C1917]">
                {h.cta2} <ArrowUpRight size={16} />
              </a>
            </Magnetic>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 + D }} className="mt-16 hidden items-center gap-4 font-mono text-xs uppercase tracking-[0.25em] text-stone-500 lg:flex">
            <span className="relative h-12 w-px overflow-hidden bg-[#D6CEBF]"><span className="scroll-line absolute inset-x-0 top-0 h-1/2 bg-[#C85A32]" /></span>
            {h.scroll}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5 [perspective:1200px]">
          <motion.div
            style={{ rotateX: rx, rotateY: ry }}
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.3 + D }}
            className="relative aspect-[4/5] overflow-hidden rounded-t-full bg-[#EFEAE1] shadow-[0_40px_80px_-30px_rgba(28,25,23,0.45)]"
          >
            <motion.img style={{ y: imgY }} src={IMG.giovani} alt="Giovani partecipanti ai progetti di Lunghe Frequenze" className="absolute inset-0 h-[125%] w-full -translate-y-[12%] object-cover" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_35%,rgba(28,25,23,0.35)_100%)]" />
          </motion.div>
          <motion.div style={{ y: floatY }} className="absolute -right-3 top-[58%] z-10 hidden w-40 sm:block lg:-right-10 lg:w-48">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 1 + D }}
              className="aspect-[3/4] overflow-hidden rounded-sm border-4 border-[#FBF9F5] shadow-[0_30px_60px_-20px_rgba(28,25,23,0.5)]"
            >
              <img src={IMG.europa} alt="" className="h-full w-full object-cover" />
            </motion.div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.3 + D }}
            className="absolute -bottom-6 -left-4 flex h-28 w-28 items-center justify-center rounded-full bg-[#C85A32] sm:-left-8 sm:h-32 sm:w-32"
          >
            <svg viewBox="0 0 100 100" className="spin-slow absolute inset-0 h-full w-full">
              <defs><path id="circ" d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0" /></defs>
              <text className="fill-[#FBF9F5] font-mono text-[9px] uppercase tracking-[0.2em]"><textPath href="#circ">{h.badge}</textPath></text>
            </svg>
            <span className="font-serif text-2xl italic text-[#FBF9F5]">APS</span>
          </motion.div>
        </div>
      </div>
      <div className="h-24 lg:h-0" />
    </section>
  );
};

const Intro = () => {
  const { t } = useLang();
  const i = t.intro;
  return (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-36">
    <div className="grid gap-10 lg:grid-cols-12">
      <FadeUp className="lg:col-span-3"><Eyebrow>{i.eyebrow}</Eyebrow></FadeUp>
      <FadeUp delay={0.1} className="lg:col-span-9">
        <p className="font-serif text-3xl leading-[1.2] tracking-tight text-[#1C1917] sm:text-5xl">
          {i.a}<em className="text-[#C85A32]">{i.em}</em>{i.b}
        </p>
        <Link to="/chi-siamo" data-testid="intro-chi-siamo-link" className="group mt-10 inline-flex items-center gap-2 text-sm font-medium text-[#1C1917]">
          <span className="border-b border-[#1C1917] pb-1">{i.link}</span>
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </div>
  </section>
  );
};

const BentoCard = ({ a, className, tall, compact }) => (
  <FadeUp className={className}>
    <Link to="/cosa-facciamo" data-testid={`bento-card-${a.id}`} className={`group relative flex h-full flex-col justify-end overflow-hidden rounded-sm bg-[#1C1917] p-6 sm:p-8 ${tall ? "min-h-[420px] lg:min-h-[560px]" : "min-h-[300px]"}`}>
      <img src={AREA_IMG[a.id]} alt={a.title} className="absolute inset-0 h-full w-full object-cover opacity-80 transition-[transform,opacity] duration-[1200ms] ease-out group-hover:scale-105 group-hover:opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917] via-[#1C1917]/40 to-transparent" />
      <span className="absolute right-6 top-6 font-mono text-xs text-[#FBF9F5]">{a.n}</span>
      <span className="absolute left-6 top-6 flex h-10 w-10 -translate-y-2 items-center justify-center rounded-full bg-[#FBF9F5] text-[#1C1917] opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100 sm:left-8"><ArrowUpRight size={16} /></span>
      <div className="relative">
        <h3 className="font-serif text-2xl text-[#FBF9F5] sm:text-3xl">{a.title}</h3>
        <p className={`mt-3 max-w-md text-sm leading-relaxed text-stone-200 ${compact ? "lg:hidden" : ""}`}>{a.text}</p>
      </div>
    </Link>
  </FadeUp>
);

const Bento = () => {
  const { t } = useLang();
  const b = t.bento;
  const A = t.areas.map((a, i) => ({ ...a, n: `0${i + 1}` }));
  return (
  <section data-testid="home-bento" className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-36">
    <div className="mb-14 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
      <FadeUp>
        <Eyebrow>{b.eyebrow}</Eyebrow>
        <h2 className="font-serif text-4xl tracking-tight text-[#1C1917] sm:text-6xl">{b.t1}<br /><em>{b.t2}</em></h2>
      </FadeUp>
      <FadeUp delay={0.1}>
        <Link to="/cosa-facciamo" data-testid="bento-all-link" className="group inline-flex items-center gap-2 text-sm font-medium text-[#1C1917]">
          <span className="border-b border-[#1C1917] pb-1">{b.all}</span>
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </div>
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-12 lg:grid-rows-2">
      <BentoCard a={A[0]} tall className="md:col-span-2 lg:col-span-7 lg:row-span-2" />
      <BentoCard a={A[1]} className="lg:col-span-5" />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:col-span-1 lg:col-span-5">
        <BentoCard a={A[2]} compact />
        <BentoCard a={A[3]} compact />
      </div>
    </div>
  </section>
  );
};

const Spotlight = () => {
  const { t } = useLang();
  const s = t.spotlight;
  return (
  <section className="bg-[#1C1917] py-24 lg:py-36">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:items-center">
      <FadeUp className="lg:col-span-6">
        <div className="relative aspect-[4/3] overflow-hidden rounded-sm">
          <img src={IMG.outdoor} alt="Esperienze internazionali e attività outdoor" className="h-full w-full object-cover" />
        </div>
      </FadeUp>
      <FadeUp delay={0.1} className="lg:col-span-5 lg:col-start-8">
        <Eyebrow light>{s.eyebrow}</Eyebrow>
        <h2 className="font-serif text-4xl leading-[1.05] tracking-tight text-[#FBF9F5] sm:text-6xl">{s.title}</h2>
        <p className="mt-6 text-base leading-relaxed text-stone-300 sm:text-lg">{s.text}</p>
        <a href={BLOG_URL} target="_blank" rel="noopener noreferrer" data-testid="spotlight-blog-btn" className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[#C85A32] px-7 py-4 text-sm font-medium text-[#FBF9F5] transition-colors duration-300 hover:bg-[#B24C26]">
          {s.cta} <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </FadeUp>
    </div>
  </section>
  );
};

export default function Home() {
  const { t } = useLang();
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Intro />
      <Bento />
      <ScrollReveal />
      <Spotlight />
      <CtaBand title={t.homeCta.title} to="/contatti" label={t.homeCta.label} testId="home-cta-contatti" />
    </main>
  );
}
